/**
 * Write or edit one journal entry.
 *
 * A bottom sheet rather than a route, so adding an entry is genuinely one tap
 * from the list and never loses the reader's place in it.
 *
 * Two kinds are authorable here. 'reading-saved' and 'prediction-rated' are
 * written by the app when you keep a reading or grade a signal — they carry
 * provenance the user cannot type, so this editor lets you retitle and annotate
 * them but not invent them.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JournalEntry, JournalEntryKind, LifeEventType, Mood } from '@astrally/shared';
import { dateInputToISO, isoToDateInput, normaliseTags, type JournalDraft } from './journalStore';

export const KIND_LABEL: Record<JournalEntryKind, string> = {
  note: 'Note',
  'life-event': 'Life event',
  'reading-saved': 'Saved reading',
  'prediction-rated': 'Rated prediction',
};

/** The kinds a person can create by hand. The other two are earned, not typed. */
const AUTHORABLE: JournalEntryKind[] = ['note', 'life-event'];

export const LIFE_EVENT_LABEL: Record<LifeEventType, string> = {
  'major-move': 'Moved city or country',
  'first-job': 'First job',
  'career-shift': 'Career shift',
  marriage: 'Marriage',
  'relationship-start': 'Relationship started',
  separation: 'Break-up or separation',
  'education-milestone': 'Education milestone',
  'sibling-event': 'Something big for a sibling',
  'family-event': 'Family event',
  'accident-or-surgery': 'Accident or surgery',
  'business-launch': 'Started something of my own',
};

const LIFE_EVENT_TYPES = Object.keys(LIFE_EVENT_LABEL) as LifeEventType[];

const MOODS: { value: Mood; glyph: string; label: string }[] = [
  { value: 1, glyph: '⛆', label: 'Rough' },
  { value: 2, glyph: '☁', label: 'Low' },
  { value: 3, glyph: '◐', label: 'Even' },
  { value: 4, glyph: '☼', label: 'Good' },
  { value: 5, glyph: '✧', label: 'Great' },
];

const SHEET: CSSProperties = {
  width: '100%',
  maxWidth: 560,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  // The composer rule: ride above the on-screen keyboard when it is open.
  paddingBottom: 'calc(16px + var(--safe-bottom) + var(--kb))',
  maxHeight: '88vh',
  overflowY: 'auto',
  outline: 'none',
};

export function EntryEditor({
  entry,
  initialKind = 'note',
  onSave,
  onDelete,
  onClose,
}: {
  /** Editing an existing entry, or undefined to write a new one. */
  entry?: JournalEntry | null;
  initialKind?: JournalEntryKind;
  onSave: (draft: JournalDraft) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<JournalEntryKind>(entry?.kind ?? initialKind);
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [dateValue, setDateValue] = useState(
    isoToDateInput(entry?.atISO ?? new Date().toISOString()),
  );
  const [mood, setMood] = useState<Mood | undefined>(entry?.mood);
  const [tags, setTags] = useState<string[]>(entry?.tags ?? []);
  const [tagDraft, setTagDraft] = useState('');
  const [lifeEventType, setLifeEventType] = useState<LifeEventType | undefined>(
    entry?.lifeEventType,
  );
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    sheetRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commitTag = () => {
    const next = normaliseTags([...tags, tagDraft]);
    if (next.length > tags.length) setTags(next);
    setTagDraft('');
  };

  /**
   * The CTA is never disabled. A dead primary button reads as a broken app —
   * it stays pressable and answers the tap by naming exactly what is missing.
   */
  const save = async () => {
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      setMessage('Give it a title first — three words is plenty.');
      titleRef.current?.focus();
      return;
    }
    if (!dateValue) {
      setMessage('Pick the date this is about. You can backdate as far as you like.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await onSave({
        atISO: dateInputToISO(dateValue),
        kind,
        title: trimmed,
        body: body.trim() || undefined,
        tags: normaliseTags(tagDraft ? [...tags, tagDraft] : tags),
        mood,
        lifeEventType: kind === 'life-event' ? lifeEventType : undefined,
        linkedReadingKey: entry?.linkedReadingKey,
        linkedSystem: entry?.linkedSystem,
        linkedSignalId: entry?.linkedSignalId,
      });
      onClose();
    } catch (e) {
      setMessage(String(e).replace(/^(FirebaseError|Error):\s*/, '').slice(0, 200));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onDelete) return;
    if (!confirm('Delete this entry? It only lives on this device, so it cannot be recovered.')) {
      return;
    }
    setBusy(true);
    await onDelete();
    onClose();
  };

  const authored = AUTHORABLE.includes(kind);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 96,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="entry-editor-title"
        tabIndex={-1}
        className="card card--strong fade-in"
        style={SHEET}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spread" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
          <div className="h2" id="entry-editor-title">
            {entry ? 'Edit entry' : 'New entry'}
          </div>
          <button className="btn btn--ghost btn--small" aria-label="Close" onClick={onClose}>
            <span aria-hidden>✕</span>
          </button>
        </div>

        {authored ? (
          <div className="seg" style={{ marginBottom: 12 }}>
            {AUTHORABLE.map((k) => (
              <button
                key={k}
                type="button"
                className={`seg__opt${kind === k ? ' seg__opt--on' : ''}`}
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
        ) : (
          <div className="banner banner--info" style={{ marginBottom: 12 }}>
            {KIND_LABEL[kind]} — Astrally wrote this one for you. Edit the words freely; what it is
            linked to stays put so your accuracy record keeps its receipts.
          </div>
        )}

        {kind === 'life-event' && (
          <div className="tiny muted" style={{ marginBottom: 10, lineHeight: 1.5 }}>
            Dated life events do double duty: they anchor your timeline, and if your birth time is
            fuzzy they give the birth-time refiner something real to score against.
          </div>
        )}

        <div className="col" style={{ gap: 12 }}>
          <label className="field">
            Title
            <input
              ref={titleRef}
              className="input"
              value={title}
              maxLength={120}
              placeholder={kind === 'life-event' ? 'Moved to Bengaluru' : 'What is on your mind?'}
              onChange={(e) => {
                setTitle(e.target.value);
                if (message) setMessage('');
              }}
            />
          </label>

          <label className="field">
            Date this is about
            <input
              className="input"
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
            />
          </label>

          {kind === 'life-event' && (
            <label className="field">
              Kind of event (optional)
              <select
                className="input select"
                value={lifeEventType ?? ''}
                onChange={(e) =>
                  setLifeEventType(e.target.value ? (e.target.value as LifeEventType) : undefined)
                }
              >
                <option value="">Not sure / something else</option>
                {LIFE_EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {LIFE_EVENT_LABEL[type]}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="field">
            Notes (optional)
            <textarea
              className="input"
              rows={4}
              value={body}
              maxLength={4000}
              placeholder="Only you ever read this. It is never used for matching."
              style={{ resize: 'vertical', lineHeight: 1.5 }}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          <div className="field">
            How was it?
            <div className="wrap" style={{ marginTop: 2 }}>
              {MOODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={`chip${mood === m.value ? ' chip--on' : ''}`}
                  aria-pressed={mood === m.value}
                  onClick={() => setMood(mood === m.value ? undefined : m.value)}
                >
                  <span aria-hidden>{m.glyph}</span>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            Tags
            <div className="row" style={{ alignItems: 'stretch' }}>
              <input
                className="input"
                value={tagDraft}
                maxLength={24}
                placeholder="work, family, health…"
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    commitTag();
                  }
                }}
              />
              <button type="button" className="btn btn--ghost btn--small" onClick={commitTag}>
                Add
              </button>
            </div>
            {tags.length > 0 && (
              <div className="wrap" style={{ marginTop: 8 }}>
                {tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="chip chip--on"
                    aria-label={`Remove tag ${tag}`}
                    onClick={() => setTags(tags.filter((x) => x !== tag))}
                  >
                    #{tag}
                    <span aria-hidden style={{ color: 'var(--text-faint)' }}>
                      ✕
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className="banner banner--danger" style={{ marginTop: 12 }} role="alert">
            {message}
          </div>
        )}

        <div className="row" style={{ marginTop: 16 }}>
          <button
            className={`btn btn--block ${title.trim() ? 'btn--primary' : 'btn--incomplete'}`}
            onClick={save}
          >
            {busy ? <span className="spinner" /> : entry ? 'Save changes' : 'Add to journal'}
          </button>
        </div>
        {entry && onDelete && (
          <button
            className="btn btn--danger btn--small btn--block"
            style={{ marginTop: 8 }}
            onClick={remove}
          >
            Delete entry
          </button>
        )}
      </div>
    </div>
  );
}
